/**
 * Generic segmented option picker (card-preferences design § Controls) — the language-selector.js
 * shape (buttons, `aria-pressed`, a section label) generalized: knows nothing about cards, sort
 * order, or rank style. `createSegmentedPicker({label, options: [{value, icon, label}], active,
 * onSelect})` builds the section; `card-preference-pickers.js` owns what the options mean.
 *
 * Both text tiers are optional (card-preference-pickers-redesign brief § 2 — show-don't-tell: the
 * card faces are the choice, no label reads them out). Omit the top-level `label` for a labelless
 * section (no visible `.segmented-picker-label`, no `role="group"`/`aria-label` on the options row —
 * an unnamed group role is a screen-reader nesting level that buys nothing, found in review).
 * Omit an option's own `label` for an icon-only button; it then MUST carry `ariaLabel` instead,
 * which becomes both the button's accessible name and its `data-tooltip` (icon-only controls keep
 * their own aria-label — the tooltip is a description, never the sole accessible name,
 * docs/components/TERMS.md). `language-selector.js`'s pending migration
 * (todo/migrate-language-selector-to-segmented-picker.md) keeps passing both tiers unchanged —
 * this doesn't foreclose it, it's the same contract with more of it now optional.
 *
 * Self-manages its own selection state: a click flips `aria-pressed` across its own buttons
 * before calling `onSelect`. `language-selector.js` never faced this — its sole caller always
 * `location.reload()`s, so the rebuilt page re-derives the active state from scratch — but the
 * start-screen mounts here don't reload, so the control has to show the new selection itself
 * rather than depend on a rebuild.
 */
function el(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function buildOption(option, isActive, onPick) {
  const btn = el("button", "segmented-picker-option");
  btn.type = "button";
  btn.setAttribute("aria-pressed", String(isActive));
  btn.dataset.value = option.value;
  option.icon.classList.add("segmented-picker-option-icon");
  btn.appendChild(option.icon);
  if (option.label != null) {
    btn.appendChild(el("span", "segmented-picker-option-label", option.label));
  } else {
    // Icon-only: both attributes carry the same text (docs/components/TERMS.md's paired
    // convention) — the button's accessible name doubles as the hover/focus tooltip.
    btn.setAttribute("aria-label", option.ariaLabel);
    btn.dataset.tooltip = option.ariaLabel;
  }
  btn.addEventListener("click", () => onPick(option.value, btn));
  return btn;
}

/**
 * Re-presses a built section's own `aria-pressed` state to `activeValue` — for a mount that can go
 * stale via a sibling mount changing the same underlying pref rather than this section's own click
 * (rulings round 1 § Item 2, scratch/rulings-round-1.md: the game-start inline card-preference
 * pickers and the start-screen menu's own copy read/write the same localStorage-backed pref but are
 * separate DOM subtrees). Visual-only — never calls `onSelect`: the pref is already the new value,
 * so firing `onSelect` again would re-apply its side effect a second time.
 */
export function pressSegmentedPicker(section, activeValue) {
  for (const btn of section.querySelectorAll(".segmented-picker-option")) {
    btn.setAttribute("aria-pressed", String(btn.dataset.value === activeValue));
  }
}

export function createSegmentedPicker({ label, options, active, onSelect }) {
  const section = el("div", "segmented-picker");
  if (label != null)
    section.appendChild(el("div", "segmented-picker-label", label));

  const optionsEl = el("div", "segmented-picker-options");
  // role="group" needs the aria-label right beside it to be worth anything — an unnamed group is
  // just an extra announced nesting level a screen reader has to pass through for no information
  // (found in review). Icon-only options each carry their own full accessible name (ariaLabel)
  // regardless, so a labelless picker loses nothing by skipping the group wrapper entirely.
  if (label != null) {
    optionsEl.setAttribute("role", "group");
    optionsEl.setAttribute("aria-label", label);
  }
  // Re-picking the active option is a no-op — language-selector.js#selectLanguage's precedent,
  // this control's own shape generalized. Without it, clicking the already-selected option still
  // fires onSelect: an unnecessary location.reload() from a menu picker, or unnecessary
  // cache-invalidation idle work from a start-screen one.
  //
  // The "currently active" read comes from the DOM's own aria-pressed, not a separate `let
  // currentActive` closure — a closure copy is a second place selection state can live, and
  // pressSegmentedPicker (below) only ever touches the DOM half of that pair. A picker whose
  // selection is re-pressed externally (rulings round 1 § Item 2: a sibling mount changing the
  // same pref) would otherwise leave this closure holding the pre-refresh value, silently no-oping
  // the next click on what the DOM shows as the *other* option and spuriously re-firing onSelect on
  // a click of the shown-active one instead — found in review. The DOM is the one place the section
  // itself, pressSegmentedPicker, and this handler all agree on, so it's the only source read here.
  const onPick = (value, pressedBtn) => {
    const currentActive = optionsEl.querySelector(
      '.segmented-picker-option[aria-pressed="true"]',
    )?.dataset.value;
    if (value === currentActive) return;
    for (const btn of optionsEl.querySelectorAll(".segmented-picker-option")) {
      btn.setAttribute("aria-pressed", String(btn === pressedBtn));
    }
    onSelect(value);
  };
  for (const option of options) {
    optionsEl.appendChild(buildOption(option, option.value === active, onPick));
  }
  section.appendChild(optionsEl);

  return section;
}
